import { Link, useNavigate } from "react-router-dom";
import { Gavel, LayoutDashboard, PlusCircle, UserCircle, ShieldCheck, LogOut, Menu, MapPin, Phone, Mail } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLang, LanguageToggle } from "@/i18n";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import api from "@/lib/api";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [contactEmail, setContactEmail] = useState("carbidmiami@gmail.com");
  useEffect(() => {
    api.get("/settings").then((r) => {
      if (r.data?.contact_email) setContactEmail(r.data.contact_email);
    }).catch(() => {});
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const links = (
    <>
      <Link to="/panel" data-testid="nav-dashboard" className="text-sm text-slate-300 hover:text-white transition-colors">{t("Mi panel")}</Link>
      <Link to="/panel/nueva" data-testid="nav-new-bid" className="text-sm text-slate-300 hover:text-white transition-colors">{t("Nueva solicitud")}</Link>
      <Link to="/perfil" data-testid="nav-profile" className="text-sm text-slate-300 hover:text-white transition-colors">{t("Perfil")}</Link>
      {(user?.role === "admin" || user?.role === "staff") && (
        <Link to="/admin" data-testid="nav-admin" className="text-sm text-blue-400 hover:text-blue-300 transition-colors font-semibold">{t("Administración")}</Link>
      )}
    </>
  );

  return (
    <div className="min-h-screen bg-[#0B0F1A] text-slate-50">
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-[#2563EB] flex items-center justify-center">
              <Gavel className="w-5 h-5 text-white" strokeWidth={1.5} />
            </div>
            <span className="font-display font-800 text-xl font-extrabold tracking-tight">Car<span className="text-[#2563EB]">Bid</span>Miami</span>
          </Link>
          <nav className="hidden md:flex items-center gap-7">
            {user ? links : (
              <>
                <a href="/#como-funciona" className="text-sm text-slate-300 hover:text-white transition-colors">{t("Cómo funciona")}</a>
                <a href="/#tarifas" className="text-sm text-slate-300 hover:text-white transition-colors">{t("Tarifas")}</a>
                <a href="/#faq" className="text-sm text-slate-300 hover:text-white transition-colors">FAQ</a>
              </>
            )}
          </nav>
          <div className="hidden md:flex items-center gap-3">
            <LanguageToggle />
            {user ? (
              <>
                <span className="text-xs text-slate-400 flex items-center gap-1.5">
                  <UserCircle className="w-4 h-4" strokeWidth={1.5} />{user.name || user.email}
                </span>
                <Button data-testid="nav-logout" variant="outline" size="sm" onClick={handleLogout}
                  className="border-white/15 bg-transparent hover:bg-white/5 hover:text-white">
                  <LogOut className="w-4 h-4 mr-1.5" strokeWidth={1.5} />{t("Salir")}
                </Button>
              </>
            ) : (
              <>
                <Link to="/login"><Button data-testid="nav-login" variant="ghost" size="sm" className="text-slate-200 hover:text-white hover:bg-white/5">{t("Entrar")}</Button></Link>
                <Link to="/registro"><Button data-testid="nav-register" size="sm" className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-full px-5">{t("Crear cuenta")}</Button></Link>
              </>
            )}
          </div>
          <div className="md:hidden flex items-center gap-3">
            <LanguageToggle />
            <button data-testid="nav-mobile-toggle" className="text-slate-200" onClick={() => setOpen(!open)}>
              <Menu className="w-6 h-6" strokeWidth={1.5} />
            </button>
          </div>
        </div>
        {open && (
          <div className="md:hidden px-6 pb-4 flex flex-col gap-3 border-t border-white/10 pt-3">
            {user ? (
              <>
                {links}
                <button data-testid="nav-logout-mobile" onClick={handleLogout} className="text-left text-sm text-red-400">{t("Cerrar sesión")}</button>
              </>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login-mobile" className="text-sm text-slate-300">{t("Entrar")}</Link>
                <Link to="/registro" data-testid="nav-register-mobile" className="text-sm text-blue-400 font-semibold">{t("Crear cuenta")}</Link>
              </>
            )}
          </div>
        )}
      </header>
      <main>{children}</main>
      <footer className="border-t border-white/10 mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-10 flex flex-col md:flex-row items-start justify-between gap-6">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#2563EB] flex items-center justify-center">
              <Gavel className="w-4 h-4 text-white" strokeWidth={1.5} />
            </div>
            <div>
              <p className="font-display font-bold">CarBidMiami</p>
              <p className="text-xs text-slate-400">{t("Bróker licenciado de Copart. Pujamos por ti.")}</p>
            </div>
          </div>
          <div className="text-xs text-slate-500 max-w-md leading-relaxed">
            {t("Depósitos 100% reembolsables si no ganamos tu subasta (aplica cuota de procesamiento de tarjeta divulgada). CarBid opera como bróker: las pujas se ejecutan manualmente por nuestro equipo en nuestra cuenta de Copart.")}
          </div>
          <div className="text-xs text-slate-400 space-y-2.5" data-testid="footer-contact">
            <p className="font-display text-sm font-semibold text-slate-200">{t("Contacto")}</p>
            <p className="flex items-start gap-2">
              <MapPin className="w-4 h-4 text-[#2563EB] shrink-0" strokeWidth={1.5} />
              19394 SW 106 Ave, Miami, FL 33157
            </p>
            <a href="tel:+17868773988" data-testid="footer-phone" className="flex items-center gap-2 hover:text-white transition-colors">
              <Phone className="w-4 h-4 text-[#2563EB] shrink-0" strokeWidth={1.5} />
              786-877-3988
            </a>
            <a href={`mailto:${contactEmail}`} data-testid="footer-email" className="flex items-center gap-2 hover:text-white transition-colors">
              <Mail className="w-4 h-4 text-[#2563EB] shrink-0" strokeWidth={1.5} />
              {contactEmail}
            </a>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <ShieldCheck className="w-4 h-4 text-[#22C55E]" strokeWidth={1.5} />
            {t("Pagos seguros con Stripe")}
          </div>
        </div>
      </footer>
    </div>
  );
}
